/**
 * 과오납관리 PoC
 * - 수납금액 > 결의금액 후보 자동로드 · CRUD · 승인요청/승인
 */

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { PageContainer } from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import {
  FormArea,
  CollapsibleSearchPanel,
  ActionButtonGroup,
  getCurrentFiscalYear,
} from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import { showAlert, showConfirm } from 'utils/toastBridge';
import {
  STATUS,
  YN_OPTIONS,
  createOverpayUid,
  emptyOverpayRow,
  loadOverpays,
  saveOverpays,
  loadOverpayCandidates,
  requestOverpayApproval,
  approveOverpay,
  statusLabel,
  todayYmd,
} from 'income/incomeStore';

const GRID_ID = 'income-overpay';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-height: 0;
  height: 100%;
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.sm};
  overflow: hidden;
  background: ${theme.colors.white};
`;

const GridBody = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

const Hint = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.secondary};
  padding: 0 2px;
`;

export default function IncomeOverpayment() {
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: { fiscalYear: getCurrentFiscalYear() },
  });
  const { getValues, reset } = methods;

  const gridRef = useRef(null);

  const columns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '수납번호', field: 'receiptNo', size: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '수입결의', field: 'incomeResolutionNo', size: 90, align: 'center' }),
      hiCol.edNumberColumn({ header: '과오납액', field: 'overAmt', size: 100, align: 'right', editable: true }),
      hiCol.edDateColumn({ header: '처리일', field: 'processDate', size: 110, align: 'center', editable: true }),
      hiCol.edTextColumn({ header: '사유', field: 'reason', size: 140, editable: true }),
      hiCol.edComboColumn({ header: '환불', field: 'refundYn', size: 70, options: YN_OPTIONS, editable: true }),
      hiCol.roTextColumn({ header: '상태', field: 'statusNm', size: 80, align: 'center' }),
      hiCol.roTextColumn({ header: '전표', field: 'slipYn', size: 60, align: 'center' }),
    ],
    []
  );

  const grid = useHiGridHook({
    gridRef,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: emptyOverpayRow({ processDate: todayYmd(), mod: 'A' }),
  });

  const dims = useCallback(
    () => ({
      tenantCode,
      fiscalYear: String(getValues('fiscalYear') || '').trim(),
    }),
    [getValues, tenantCode]
  );

  const handleSearch = useCallback(async () => {
    const d = dims();
    if (!d.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    const saved = (await loadOverpays(d)).filter((r) => r.mod !== 'D');
    const candidates = await loadOverpayCandidates(d);
    const byReceipt = new Map(saved.map((r) => [r.receiptUid, r]));
    candidates.forEach((c) => {
      if (!byReceipt.has(c.receiptUid)) byReceipt.set(c.receiptUid, c);
    });
    const rows = Array.from(byReceipt.values()).map((r) => ({
      ...r,
      statusNm: statusLabel(r.status),
    }));
    gridRef.current?.setData?.(rows);
  }, [dims]);

  useEffect(() => {
    reset({ fiscalYear: getCurrentFiscalYear() });
  }, [reset]);

  const handleAdd = useCallback(async () => {
    const row = emptyOverpayRow({
      overpayUid: createOverpayUid(),
      processDate: todayYmd(),
      status: STATUS.DRAFT,
      mod: 'A',
    });
    gridRef.current?.setData?.([...(gridRef.current?.getData?.() || []), row]);
  }, []);

  const handleSave = useCallback(async () => {
    const d = dims();
    if (!d.fiscalYear) {
      showAlert('회계연도를 입력하세요.');
      return;
    }
    const gridRows = gridRef.current?.getData?.() || [];
    const all = await loadOverpays(d);
    const byUid = new Map(all.map((r) => [r.overpayUid, r]));

    gridRows.forEach((row) => {
      const uid = row.overpayUid || createOverpayUid();
      const prev = byUid.get(uid);
      byUid.set(uid, {
        ...emptyOverpayRow(),
        ...(prev || {}),
        ...row,
        overpayUid: uid,
        mod: prev ? (row.mod === 'D' ? 'D' : 'U') : row.mod || 'A',
      });
    });

    await saveOverpays(d, Array.from(byUid.values()));
    await handleSearch();
    showAlert('저장했습니다.', 'success');
  }, [dims, handleSearch]);

  const handleRequest = useCallback(async () => {
    const sel = gridRef.current?.getSelectedRow?.();
    if (!sel?.overpayUid) {
      showAlert('승인요청할 과오납 건을 선택하세요.');
      return;
    }
    if (sel.status !== STATUS.DRAFT && sel.status !== STATUS.REJECTED) {
      showAlert('작성중·반려 상태만 승인요청할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('승인요청할까요?');
    if (!ok) return;
    await handleSave();
    const d = dims();
    const all = await loadOverpays(d);
    const next = requestOverpayApproval(all, sel.overpayUid);
    await saveOverpays(d, next);
    await handleSearch();
    showAlert('승인요청했습니다.', 'success');
  }, [handleSave, dims, handleSearch]);

  const handleApprove = useCallback(async () => {
    const sel = gridRef.current?.getSelectedRow?.();
    if (!sel?.overpayUid) {
      showAlert('승인할 과오납 건을 선택하세요.');
      return;
    }
    if (sel.status !== STATUS.REQUESTED) {
      showAlert('승인요청 상태만 승인할 수 있습니다.');
      return;
    }
    const ok = await showConfirm('승인할까요? (전표 stub)');
    if (!ok) return;
    const d = dims();
    const all = await loadOverpays(d);
    const next = approveOverpay(all, sel.overpayUid);
    await saveOverpays(d, next);
    await handleSearch();
    showAlert('승인했습니다.', 'success');
  }, [dims, handleSearch]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'fiscalYear',
          label: '회계연도',
          type: 'fiscalYear',
          width: '88px',
          labelWidth: SEARCH_LABEL_W,
        },
      ],
    ],
    []
  );

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      { id: 'add', label: '추가', icon: '➕', onClick: handleAdd, hotkey: 'F8' },
      {
        id: 'delete',
        label: '삭제',
        icon: '❌',
        onClick: () => grid.handleDelete?.(),
        hotkey: 'F9',
        variant: 'danger',
      },
      { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
    ],
    [handleSearch, handleAdd, handleSave, grid]
  );

  const extraButtons = useMemo(
    () => [
      { id: 'request', label: '승인요청', onClick: handleRequest },
      { id: 'approve', label: '승인', onClick: handleApprove, variant: 'primary' },
    ],
    [handleRequest, handleApprove]
  );

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <Stack>
          <CollapsibleSearchPanel storageKey="income-overpay" prefsUserId={prefsUserId} title="조회조건">
            <FormArea compact fields={searchFields} />
          </CollapsibleSearchPanel>

          <Hint>수납금액 &gt; 결의금액 시 후보 자동표시 · 수동 추가 가능</Hint>

          <Panel>
            <GridBody>
              <HiGridComponent
                ref={gridRef}
                columns={columns}
                showRowNumber
                rowNumberSize={48}
                enableColumnFilters
                enableContextMenu
                enableRowEditMenu
                enableCut
                enableFindReplace
                allowReplace
                enableHeaderMenu
                enableSelectionStats
                enableQuickExport
                exportFileName="과오납"
                gridId={GRID_ID}
                prefsUserId={prefsUserId}
                {...(grid.contextMenuProps || {})}
              />
            </GridBody>
          </Panel>
        </Stack>
      </FormProvider>
    </PageContainer>
  );
}
